/**
 * CclSentOffSystem — BM-H160 third option: "CCL and welcome pack sent off CaseMinder".
 *
 * Outbound email from CaseMinder has recorded no sends since 14 May 2026, so the
 * route where the client clicks a link in a CaseMinder email is unreachable and
 * the team sends CCLs by hand. This box records that.
 *
 * It deliberately shows TWO facts, never one. Sending the pack is not the client
 * accepting it, and the client's acceptance is the legal moment of engagement.
 * So a matter recorded as sent with no confirmation shows AMBER and says
 * "not accepted". It only goes green once someone records that the client has
 * actually confirmed, and how. Both records can be undone by a member of staff.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

function today() {
  return new Date().toISOString().slice(0, 10);
}

// BM-H3913: acceptance without a date is only for packs sent before this day
// (legacy hand-sent CCLs). Mirrors LEGACY_ACCEPTANCE_SENT_BEFORE on the server,
// which is the rule; this only decides whether to offer the option.
const LEGACY_SENT_BEFORE = "2026-10-08";

const SOURCE_LABEL = {
  sent_email: "Sent email",
  received_email: "Email received",
  filed_email_file: "Filed email",
  email_attachment: "Letter from an email",
  document: "Letter",
};

function flags(c) {
  if (c.reply) return " (reply in the thread)";
  if (c.draft) return " (draft)";
  if (c.archive) return " (previous firm's archive)";
  return "";
}

function describeLook(found) {
  const bits = [];
  if (found.folder_read) {
    bits.push(`${found.files_examined} file(s) in ${found.folders_walked} folder(s) of the matter folder`);
  }
  bits.push(`${found.filings_examined || 0} filed email(s)`);
  return bits.join(" and ");
}

// BM-H4066: what the matter holds. Says what it looked at either way, and
// says so when the matter folder could not be read, so "nothing found" is
// never mistaken for "could not look".
function FoundInMatter({ found, finding, onUse }) {
  if (finding) {
    return (
      <p className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="ccl-off-system-finding">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking in the matter for the sent letter
      </p>
    );
  }
  if (!found) return null;
  const candidates = (found.candidates || []).slice(0, 6);
  return (
    <div className="mb-2 rounded border border-slate-200 bg-white p-2 text-[11px] text-slate-700" data-testid="ccl-off-system-found">
      <p className="flex items-center gap-1.5 font-semibold text-slate-800">
        <Search className="h-3.5 w-3.5" />
        {candidates.length
          ? "Found in the matter. Check the details below before recording."
          : found.could_not_look
            ? "Could not look in the matter."
            : "Nothing found in the matter. Fill in the details by hand."}
      </p>
      {candidates.length > 0 && (
        <ul className="mt-1 space-y-1" data-testid="ccl-off-system-found-list">
          {candidates.map((c, index) => (
            <li key={`${c.web_url || c.name}-${index}`} className="flex flex-wrap items-center gap-2">
              <span>
                {SOURCE_LABEL[c.source] || "Item"}{flags(c)}: {c.name}{c.date ? `, ${c.date}` : ""}
              </span>
              {c.web_url && (
                <a href={c.web_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {c.source !== "received_email" && (
                <button type="button" onClick={() => onUse(c)} className="underline" data-testid="ccl-off-system-use-candidate">
                  Use this
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {found.suggestion?.date_basis && (
        <p className="mt-1 text-slate-500">The date filled in is {found.suggestion.date_basis}.</p>
      )}
      <p className="mt-1 text-slate-500" data-testid="ccl-off-system-found-examined">
        {found.could_not_look ? "" : `Looked at ${describeLook(found)}.`}
        {found.reason ? ` ${found.reason}` : ""}
        {found.read_errors?.length ? ` ${found.read_errors.length} folder(s) could not be read.` : ""}
      </p>
    </div>
  );
}

export const OFF_SYSTEM_EVENT = "bonsai:ccl-off-system";

export default function CclSentOffSystem({ matterRef, onChange, onState }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(Boolean(matterRef));
  const [busy, setBusy] = useState(false);
  const [showSendForm, setShowSendForm] = useState(false);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [sentDate, setSentDate] = useState(today());
  const [sentMethod, setSentMethod] = useState("");
  const [sentNotes, setSentNotes] = useState("");
  const [acceptedDate, setAcceptedDate] = useState(today());
  const [acceptedMethod, setAcceptedMethod] = useState("");
  const [legacyNoDate, setLegacyNoDate] = useState(false);
  // BM-H4066: what the matter already holds that shows the CCL went.
  const [found, setFound] = useState(null);
  const [finding, setFinding] = useState(false);
  const [touched, setTouched] = useState(false);

  const base = matterRef ? `${API}/ccl/off-system/${encodeURIComponent(matterRef)}` : "";

  const loadState = useCallback(async () => {
    if (!matterRef) return;
    try {
      setLoading(true);
      const response = await api.get(`${API}/ccl/off-system/${encodeURIComponent(matterRef)}`);
      setState(response.data?.off_system_ccl || null);
    } catch (error) {
      if (error?.response?.status !== 404) {
        toast.error(errorMessage(error, "Could not load the off-system CCL record"));
      }
    } finally {
      setLoading(false);
    }
  }, [matterRef]);

  useEffect(() => { loadState(); }, [loadState]);

  // BM-H3747 (Vanessa, 19 Oct 2026): the matter page shows this panel twice,
  // on the intake card and inside the CCL editor. A send recorded on one now
  // reaches the other at once, so the editor folds without a page refresh.
  const selfRef = useRef(Symbol("ccl-off-system"));
  const publish = useCallback((next) => {
    setState(next);
    window.dispatchEvent(new CustomEvent(OFF_SYSTEM_EVENT, {
      detail: { matterRef, state: next, source: selfRef.current },
    }));
  }, [matterRef]);
  useEffect(() => {
    if (!matterRef) return undefined;
    const onOther = (event) => {
      const d = event.detail || {};
      if (d.source !== selfRef.current && d.matterRef === matterRef) setState(d.state);
    };
    window.addEventListener(OFF_SYSTEM_EVENT, onOther);
    return () => window.removeEventListener(OFF_SYSTEM_EVENT, onOther);
  }, [matterRef]);
  // BM-H3747 (8 Oct 2026): the CCL editor folds away once the letter is sent,
  // and an off-system send is only known here.
  useEffect(() => { if (onState && !loading) onState(state); }, [state, loading, onState]);

  // BM-H4066 (Vanessa, 8 Oct 2026): "can we get the system to look in the
  // matter for this?" Opening the form looks in the matter's filed emails and
  // SharePoint folder and starts the form from the best item found. Staff
  // still check it and press "Record as sent". Anything they have already
  // typed is never overwritten.
  const applySuggestion = (suggestion) => {
    if (!suggestion) return;
    if (suggestion.sent_date) setSentDate(suggestion.sent_date);
    setSentMethod(suggestion.method || "");
    setSentNotes(suggestion.notes || "");
  };

  const useCandidate = (candidate) => {
    const email = ["sent_email", "filed_email_file", "email_attachment"].includes(candidate.source);
    const where = candidate.path || "the matter folder";
    if (candidate.date) setSentDate(candidate.date);
    setSentMethod(email ? "Email from Outlook" : "");
    setSentNotes(candidate.source === "email_attachment"
      ? `Letter saved from an email, found in the matter folder at ${where}`
      : email
        ? `Email "${candidate.name}" found in the matter, saved in ${where}${candidate.sent_by ? `, sent by ${candidate.sent_by}` : ""}`
        : `Letter found in the matter folder at ${where}`);
    setTouched(true);
  };

  useEffect(() => {
    if (!showSendForm || !base) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setFinding(true);
        const response = await api.get(`${base}/found`);
        if (cancelled) return;
        setFound(response.data || null);
      } catch (error) {
        // A 404 is a backend without this route yet (the frontend goes live on
        // merge, the backend at the evening swap) or a matter with no
        // onboarding record. Either way the form simply starts blank.
        const reason = error?.response?.status === 404
          ? "The matter could not be searched yet, so fill in the details by hand."
          : errorMessage(error, "Could not look in the matter");
        if (!cancelled) setFound({ reason, candidates: [], could_not_look: true });
      } finally {
        if (!cancelled) setFinding(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSendForm, base]);

  useEffect(() => {
    if (found?.suggestion && !touched) applySuggestion(found.suggestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found]);

  // BM-H4123: each record files an attendance note in the matter's
  // Attendances folder. A note that could not be filed is said, not hidden.
  const reportNote = (note) => {
    if (!note) return;
    if (note.filed) {
      toast.success("Attendance note filed in the matter's Attendances folder.");
    } else {
      toast.warning(`The record is saved, but the attendance note was not filed: ${note.reason || "reason unknown"}`);
    }
  };

  const recordSent = async () => {
    if (!sentMethod.trim()) {
      toast.error("Say how the CCL and welcome pack were sent");
      return;
    }
    try {
      setBusy(true);
      const response = await api.post(`${base}/sent`, {
        sent_date: sentDate,
        method: sentMethod.trim(),
        notes: sentNotes.trim() || undefined,
      });
      publish(response.data?.off_system_ccl || null);
      setShowSendForm(false);
      setSentMethod("");
      setSentNotes("");
      setFound(null);
      setTouched(false);
      if (onChange) onChange();
      toast.success("Recorded as sent off CaseMinder. Client acceptance is still outstanding.");
      reportNote(response.data?.attendance_note);
    } catch (error) {
      toast.error(errorMessage(error, "Could not record the off-system send"));
    } finally {
      setBusy(false);
    }
  };

  const recordAccepted = async () => {
    if (!acceptedMethod.trim()) {
      toast.error(legacyNoDate ? "Say what shows the client went ahead" : "Say how the client confirmed");
      return;
    }
    try {
      setBusy(true);
      const response = await api.post(`${base}/accepted`, {
        accepted_date: legacyNoDate ? "" : acceptedDate,
        method: acceptedMethod.trim(),
        legacy_no_date: legacyNoDate,
      });
      publish(response.data?.off_system_ccl || null);
      setShowAcceptForm(false);
      setAcceptedMethod("");
      setLegacyNoDate(false);
      if (onChange) onChange();
      toast.success("Client confirmation recorded. The matter now reads as engaged.");
      reportNote(response.data?.attendance_note);
    } catch (error) {
      toast.error(errorMessage(error, "Could not record the client confirmation"));
    } finally {
      setBusy(false);
    }
  };

  const undo = async (which) => {
    try {
      setBusy(true);
      const response = await api.delete(`${base}/${which}`);
      publish(response.data?.off_system_ccl || null);
      if (onChange) onChange();
      toast.success(which === "accepted" ? "Client confirmation removed" : "Off-system send record removed");
    } catch (error) {
      toast.error(errorMessage(error, "Could not undo that record"));
    } finally {
      setBusy(false);
    }
  };

  const status = state?.display_status || "not_recorded";
  const accepted = status === "accepted";
  const sentNotAccepted = status === "sent_not_accepted";
  const legacyAllowed = Boolean(state?.sent_date) && state.sent_date < LEGACY_SENT_BEFORE;

  return (
    <div className="border-b border-slate-200 bg-white px-3 py-2" data-testid="ccl-off-system-panel">
      {accepted && (
        <div
          className="mb-2 flex flex-wrap items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
          data-testid="ccl-off-system-accepted-banner"
          data-status="accepted"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span className="font-medium">
            CCL and welcome pack sent off CaseMinder on {state.sent_date} ({state.sent_method}).
            {state.accepted_legacy_no_date
              ? ` Legacy matter: acceptance date not recorded. Client went ahead (${state.accepted_method}).`
              : ` Client confirmed on ${state.accepted_date} (${state.accepted_method}).`}
          </span>
          <button
            type="button"
            onClick={() => undo("accepted")}
            disabled={busy}
            className="ml-auto underline hover:text-emerald-950"
            data-testid="ccl-off-system-undo-accepted-btn"
          >
            Undo confirmation
          </button>
        </div>
      )}

      {sentNotAccepted && (
        <div
          className="mb-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="ccl-off-system-sent-banner"
          data-status="sent_not_accepted"
        >
          <div className="flex flex-wrap items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="font-medium">
              Sent off CaseMinder on {state.sent_date} by {state.sent_recorded_by} ({state.sent_method}).
              Client acceptance not confirmed, so this matter is not engaged yet.
              {state.sent_notes ? ` Note: ${state.sent_notes}` : ""}
            </span>
            <button
              type="button"
              onClick={() => undo("sent")}
              disabled={busy}
              className="ml-auto underline hover:text-amber-950"
              data-testid="ccl-off-system-undo-sent-btn"
            >
              Undo
            </button>
          </div>
          {!showAcceptForm && (
            <button
              type="button"
              onClick={() => setShowAcceptForm(true)}
              className="mt-2 inline-flex items-center gap-1.5 rounded bg-emerald-700 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-600"
              data-testid="ccl-off-system-open-accept-btn"
            >
              Record client confirmation
            </button>
          )}
        </div>
      )}

      {status === "not_recorded" && !showSendForm && (
        <button
          type="button"
          onClick={() => setShowSendForm(true)}
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          data-testid="ccl-off-system-open-send-btn"
        >
          <Send className="h-3.5 w-3.5" /> CCL and welcome pack sent off CaseMinder
        </button>
      )}

      {showSendForm && (
        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-3" data-testid="ccl-off-system-send-form">
          <p className="mb-2 text-xs font-semibold text-slate-800">
            Record that the CCL and welcome pack were sent outside CaseMinder
          </p>
          <FoundInMatter found={found} finding={finding} onUse={useCandidate} />
          <label className="block text-xs text-slate-700">
            Date it was sent
            <input
              type="date"
              value={sentDate}
              onChange={(event) => { setSentDate(event.target.value); setTouched(true); }}
              className="mt-1 block w-full rounded border border-slate-300 bg-white p-1.5 text-xs"
              data-testid="ccl-off-system-sent-date"
            />
          </label>
          <label className="mt-2 block text-xs text-slate-700">
            How it was sent
            <input
              type="text"
              value={sentMethod}
              onChange={(event) => { setSentMethod(event.target.value); setTouched(true); }}
              placeholder="Email from Outlook, post, hand delivery"
              className="mt-1 block w-full rounded border border-slate-300 bg-white p-1.5 text-xs"
              data-testid="ccl-off-system-sent-method"
            />
          </label>
          {/* BM-H3443: optional note, e.g. where the sent letter is saved. */}
          <label className="mt-2 block text-xs text-slate-700">
            Note (optional)
            <input
              type="text"
              value={sentNotes}
              onChange={(event) => { setSentNotes(event.target.value); setTouched(true); }}
              placeholder="Where the sent letter is saved, who sent it"
              className="mt-1 block w-full rounded border border-slate-300 bg-white p-1.5 text-xs"
              data-testid="ccl-off-system-sent-notes"
            />
          </label>
          <p className="mt-2 text-[11px] text-slate-500">
            This records what the firm did. It does not record the client accepting, so the matter
            will not read as engaged until you record the client's confirmation separately.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={recordSent}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded bg-teal px-2.5 py-1.5 text-xs font-medium text-white hover:bg-teal/90 disabled:opacity-50"
              data-testid="ccl-off-system-save-sent-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Record as sent
            </button>
            <button
              type="button"
              onClick={() => setShowSendForm(false)}
              className="text-xs text-slate-500 underline"
              data-testid="ccl-off-system-cancel-sent-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAcceptForm && (
        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-3" data-testid="ccl-off-system-accept-form">
          <p className="mb-2 text-xs font-semibold text-slate-800">Record the client's confirmation</p>
          {legacyAllowed && (
            <label className="mb-2 flex items-start gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={legacyNoDate}
                onChange={(event) => setLegacyNoDate(event.target.checked)}
                className="mt-0.5"
                data-testid="ccl-off-system-legacy-no-date"
              />
              <span>
                Legacy matter: the client went ahead but the date they accepted was never recorded.
                The matter history will say the acceptance date is not recorded.
              </span>
            </label>
          )}
          {!legacyNoDate && (
            <label className="block text-xs text-slate-700">
              Date the client confirmed
              <input
                type="date"
                value={acceptedDate}
                onChange={(event) => setAcceptedDate(event.target.value)}
                className="mt-1 block w-full rounded border border-slate-300 bg-white p-1.5 text-xs"
                data-testid="ccl-off-system-accepted-date"
              />
            </label>
          )}
          <label className="mt-2 block text-xs text-slate-700">
            {legacyNoDate ? "What shows the client went ahead" : "How the client confirmed"}
            <input
              type="text"
              value={acceptedMethod}
              onChange={(event) => setAcceptedMethod(event.target.value)}
              placeholder={legacyNoDate
                ? "Client gave instructions and work went ahead, invoice paid"
                : "Reply by email, signed copy returned, confirmed by telephone"}
              className="mt-1 block w-full rounded border border-slate-300 bg-white p-1.5 text-xs"
              data-testid="ccl-off-system-accepted-method"
            />
          </label>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={recordAccepted}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded bg-emerald-700 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              data-testid="ccl-off-system-save-accepted-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Record confirmation
            </button>
            <button
              type="button"
              onClick={() => setShowAcceptForm(false)}
              className="text-xs text-slate-500 underline"
              data-testid="ccl-off-system-cancel-accepted-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" aria-label="Loading off-system CCL status" />}
    </div>
  );
}
